import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import KPICard from '../../components/ui/KPICard'
import Card from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import IntegrationBadge from '../../components/ui/IntegrationBadge'
import { api } from '../../services/api'

export default function Dashboard() {
  const { data, isLoading: _isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.getDashboard(),
  })

  // Demo data for display
  const kpis = data?.data?.kpis || {
    revenue: 45680,
    expenses: 28450,
    netIncome: 17230,
    cashBalance: 32500,
  }

  const reconciliation = data?.data?.reconciliation || {
    matchedPercent: 78,
    unmatchedCount: 12,
  }

  const recentTransactions = [
    { id: 1, date: '2026-01-26', description: 'SQ *GOSQ.COM REF4829573619', amount: 3245.00, status: 'confirmed', direction: 'credit', source: 'square' },
    { id: 2, date: '2026-01-26', description: 'SYSCO CORP ACH PMT', amount: -1523.45, status: 'categorized', direction: 'debit', source: 'bank' },
    { id: 3, date: '2026-01-25', description: 'DOORDASH INC PAYOUT', amount: 876.50, status: 'new', direction: 'credit', source: 'doordash' },
    { id: 4, date: '2026-01-25', description: 'PEPSICO INC ACH', amount: -432.00, status: 'confirmed', direction: 'debit', source: 'bank' },
    { id: 5, date: '2026-01-24', description: 'SQ *GOSQ.COM REF9182736450', amount: 2987.00, status: 'reconciled', direction: 'credit', source: 'square' },
    { id: 6, date: '2026-01-23', description: 'UBER *EATS PAYOUT', amount: 654.25, status: 'new', direction: 'credit', source: 'ubereats' },
  ]

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-500 mt-1">Your bookkeeping at a glance</p>
        </div>
        <div className="flex space-x-3">
          <Link to="/demo">
            <Button variant="secondary">Seed Demo Data</Button>
          </Link>
          <Button>Run Reconciliation</Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          title="Revenue"
          value={kpis.revenue}
          trend={{ value: 12.5, isPositive: true }}
          subtitle="vs last month"
        />
        <KPICard
          title="Expenses"
          value={kpis.expenses}
          trend={{ value: 3.2, isPositive: false }}
          subtitle="vs last month"
        />
        <KPICard
          title="Net Income"
          value={kpis.netIncome}
          variant="success"
          trend={{ value: 18.7, isPositive: true }}
        />
        <KPICard
          title="Cash Balance"
          value={kpis.cashBalance}
        />
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Transactions */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
              <Link to="/transactions" className="text-sm text-primary-600 hover:text-primary-700">
                View all →
              </Link>
            </div>
            <div className="space-y-4">
              {recentTransactions.map((txn) => (
                <div
                  key={txn.id}
                  className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center space-x-4">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        txn.direction === 'credit' ? 'bg-green-100' : 'bg-red-100'
                      }`}
                    >
                      <span className={txn.direction === 'credit' ? 'text-green-600' : 'text-red-600'}>
                        {txn.direction === 'credit' ? '↓' : '↑'}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-gray-900">{txn.description}</p>
                        <IntegrationBadge platform={txn.source} size="sm" />
                      </div>
                      <p className="text-sm text-gray-500">{txn.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <Badge
                      variant={
                        txn.status === 'reconciled'
                          ? 'success'
                          : txn.status === 'confirmed'
                          ? 'info'
                          : txn.status === 'categorized'
                          ? 'warning'
                          : 'default'
                      }
                    >
                      {txn.status}
                    </Badge>
                    <span
                      className={`font-semibold ${
                        txn.amount > 0 ? 'text-green-600' : 'text-gray-900'
                      }`}
                    >
                      {txn.amount > 0 ? '+' : ''}
                      {txn.amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Reconciliation Status */}
        <div className="space-y-6">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Reconciliation</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Matched</span>
                  <span className="font-medium text-gray-900">{reconciliation.matchedPercent}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-green-500 h-2.5 rounded-full"
                    style={{ width: `${reconciliation.matchedPercent}%` }}
                  />
                </div>
              </div>
              <div className="flex justify-between items-center py-3 border-t border-gray-100">
                <span className="text-gray-600">Unmatched items</span>
                <Badge variant="warning">{reconciliation.unmatchedCount}</Badge>
              </div>
            </div>
            <Link to="/reconciliation">
              <Button variant="secondary" className="w-full mt-4">
                View Details
              </Button>
            </Link>
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link to="/demo" className="block">
                <Button variant="secondary" className="w-full justify-start">
                  🎲 Generate Demo Data
                </Button>
              </Link>
              <Link to="/transactions" className="block">
                <Button variant="secondary" className="w-full justify-start">
                  📝 Categorize Transactions
                </Button>
              </Link>
              <Link to="/reports/pnl" className="block">
                <Button variant="secondary" className="w-full justify-start">
                  📊 View P&L Report
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}
